import { Routes } from '@angular/router';
import { authGuard, hasPermissionGuard, publicOnlyGuard } from '@trueform/ui/core';
import { rolesRoutes } from '@trueform/ui/roles';
import { usersRoutes } from '@trueform/ui/users';
import {
  FX_RATES_VIEW_PERMISSION,
  PRICES_VIEW_PERMISSION,
  PRICE_SOURCES_VIEW_PERMISSION,
} from '@/core/config/auth-permissions.config';
import { createDrawerChildRoutes } from '@/core/drawer-route.util';
import { FX_RATES_DRAWER_DEFS } from '@/features/fx-rates/fx-rates.drawers';
import { PRICE_SOURCES_DRAWER_DEFS } from '@/features/price-sources/price-sources.drawers';
import { PRICES_DRAWER_DEFS } from '@/features/prices/prices.drawers';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('@trueform/ui/auth').then((m) => m.LoginComponent),
  },
  {
    path: 'authenticate',
    // The screen itself moved to `@trueform/ui/auth` (Wave 3); the route
    // record stays app-side data, like the users/roles screens established.
    loadComponent: () => import('@trueform/ui/auth').then((m) => m.AuthenticateCallbackComponent),
  },
  {
    path: 'forgot-password',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('@trueform/ui/auth').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('@trueform/ui/auth').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'set-password',
    loadComponent: () => import('@trueform/ui/auth').then((m) => m.SetPasswordComponent),
  },
  // The users/roles screens and their records are library-owned (WUIAM-140):
  // `usersRoutes()` / `rolesRoutes()` carry `authGuard` + `hasPermissionGuard`,
  // `data.permission` `[ng.users|ng.roles, view]` and the lazy `loadComponent`
  // of the screen (the barrels no longer export the screen classes). The app
  // only spreads them, keeping ordinary routes as app data.
  ...usersRoutes(),
  ...rolesRoutes(),
  {
    path: 'prices',
    canActivate: [authGuard, hasPermissionGuard],
    data: {
      permission: PRICES_VIEW_PERMISSION,
    },
    // Deep-link drawers (`DESIGN-WUIAM-135` §2.2 / §3.3, DAG node (e)):
    // `/prices/new` and `/prices/:priceId/correct`. The screen's `mode`/
    // `asOfDate`/`maxAgeDays` STAY query params and ride the child navigation
    // (`queryParamsHandling: 'preserve'`), so back returns to the exact filtered
    // list and a refresh restores drawer AND list context.
    children: createDrawerChildRoutes(PRICES_DRAWER_DEFS),
    // The Price Hub screens are app-specific (PHS is not a template concern),
    // so they live under `src/app/features/` and are lazy-loaded like every
    // other record here.
    loadComponent: () =>
      import('@/features/prices/prices.component').then((m) => m.PricesComponent),
  },
  {
    path: 'price-sources',
    canActivate: [authGuard, hasPermissionGuard],
    data: {
      permission: PRICE_SOURCES_VIEW_PERMISSION,
    },
    // Deep-link drawers (`DESIGN-WUIAM-135` §2.2 / §3.3, DAG node (d)):
    // `/price-sources/new`, `/price-sources/:sourceId/history`,
    // `/price-sources/:sourceId/rename`. No plain `:id` record — this screen has
    // no edit form. Delete keeps its in-memory confirmation (no URL).
    children: createDrawerChildRoutes(PRICE_SOURCES_DRAWER_DEFS),
    loadComponent: () =>
      import('@/features/price-sources/price-sources.component').then(
        (m) => m.PriceSourcesComponent,
      ),
  },
  {
    path: 'fx-rates',
    canActivate: [authGuard, hasPermissionGuard],
    data: {
      permission: FX_RATES_VIEW_PERMISSION,
    },
    // The deep-link drawer children (`DESIGN-WUIAM-135` §2.2 / §3.3, DAG node
    // (c)): `/fx-rates/new` (register) and `/fx-rates/:fxRateId/correct`. They
    // are component-less, so the list stays the backdrop and no chunk is added;
    // the screen's dispatch effect reacts to the URL. The defs own the action
    // permission tuples (the screen's own constants — referenced, not copied).
    children: createDrawerChildRoutes(FX_RATES_DRAWER_DEFS),
    // Wave-1 stub (TASK-101 precedent); [[TASK-121-fx-rates-screen|TASK-121]]
    // replaces the component, keeping this lazy import path byte-stable.
    loadComponent: () =>
      import('@/features/fx-rates/fx-rates.component').then((m) => m.FxRatesComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@/features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
  },
  {
    path: '',
    pathMatch: 'full',
    // The prompted start url (`/dashboard` → `dashboard`): the root route and
    // the manifest `start_url` / post-login landing must agree.
    redirectTo: 'dashboard',
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@trueform/ui/layout/screens').then((m) => m.ProfileSettingsComponent),
  },
  {
    // The Catalogs SECTION gate is auth-only. The section has no
    // permission of its own (the nav group is likewise ungated — it renders
    // when any child is visible), and every leaf carries its OWN screen
    // permission (a granular child's own per-feature resource such as
    // `ng.countries:view` per ADR-018 D1). Gating the parent route
    // would silently override a granular child's gate: a
    // role holding only `ng.countries:view` saw the Countries nav child but
    // the deep link (and the nav click) was denied by the parent before the
    // child guard ran (ARCH-020 §6-D/§7-M requires it to load). Guarding the
    // leaves instead keeps the section DRY without defeating granularity.
    path: 'catalogs',
    canActivate: [authGuard],
    loadChildren: () => import('./features/catalogs/catalog.routes').then((m) => m.CATALOG_ROUTES),
  },
  {
    // The Price Calculator SECTION gate is auth-only, for the SAME reason as
    // `/catalogs` above (ADR-018 D1, TASK-299 fix-pass note, PR #57 bot triage
    // C4168851209). A parent section gate would defeat the
    // granular `ng.pcs_calculator:view` leaves: a role holding only the
    // Lite Calculator permission saw the `calculation`/`my-calculations` nav
    // children but the parent's guard denied the click (and the deep link)
    // before the child guard ran. Every leaf keeps its OWN guard — the five
    // tariff leaves + `calculation` + `my-calculations` + the `lite-calculator`
    // redirect, all self-guarded in `price-calculator.routes.ts` — and the
    // nav group auto-hides when every child is hidden, so the section needs no
    // permission of its own. WUIAM-22's granular child depends on this.
    path: 'price-calculator',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/price-calculator/price-calculator.routes').then(
        (m) => m.PRICE_CALCULATOR_ROUTES,
      ),
  },
  {
    path: '**',
    redirectTo: '',
  },
];
